import { message } from "antd";
import shiftService from "../services/shift";
import { localErrorHandler } from "../utils/errorHandler";
import { isNoAnswer } from "../services/offline/serverReach";
import { readCopy, saveCopy } from "../services/offline/offlineCopy";
import {
  getPendingShiftStart,
  localShiftOf,
  savePendingShiftStart,
} from "../services/offline/offlineShift";

// the open shift is kept on this device, so the POS can sell without internet
const shiftCopyName = () => `shift:${localStorage.getItem("shopId")}`;

const namespace = "shift";

const initialState = {
  current: null,
  loading: {
    start: false,
    fetchCurrent: false,
    addMovement: false,
    close: false,
  },
};

export const startShift = (payload) => ({
  type: `${namespace}/start`,
  payload,
});

export const fetchCurrentShift = () => ({
  type: `${namespace}/fetchCurrent`,
});

export const addShiftMovement = (shiftId, data) => ({
  type: `${namespace}/addMovement`,
  payload: { shiftId, data },
});

export const closeShift = (shiftId, data) => ({
  type: `${namespace}/close`,
  payload: { shiftId, data },
});

export default {
  namespace,
  state: initialState,
  effects: {
    *start({ payload }, { call, put, select }) {
      yield put({ type: "startLoading", loadingType: "start" });
      try {
        const response = yield call(shiftService.start, payload);
        saveCopy(shiftCopyName(), response.data || null);
        yield put({ type: "setCurrent", payload: response.data });
      } catch (error) {
        if (!isNoAnswer(error))
          localErrorHandler({ namespace, error, stopLoading: "start" });
        // no internet: the shift starts on this device and is sent first when
        // the internet is back (the offline orders then join it)
        const start = { ...payload, openedAt: new Date().toISOString() };
        savePendingShiftStart(start);
        const userId = yield select((state) => state.auth.user?.id);
        const shift = localShiftOf(start, userId);
        saveCopy(shiftCopyName(), shift);
        yield put({ type: "setCurrent", payload: shift });
        message.warning(
          "Shift started on this device. It is sent to the server when the internet is back.",
          6,
        );
      } finally {
        yield put({ type: "stopLoading", loadingType: "start" });
      }
    },
    *fetchCurrent(_, { call, put, select }) {
      yield put({ type: "startLoading", loadingType: "fetchCurrent" });
      try {
        const response = yield call(shiftService.fetchCurrent);
        // a shift started offline stays until it has been sent
        const pending = getPendingShiftStart();
        const userId = yield select((state) => state.auth.user?.id);
        const shift =
          response.data || (pending ? localShiftOf(pending, userId) : null);
        saveCopy(shiftCopyName(), shift);
        yield put({ type: "setCurrent", payload: shift });
      } catch (error) {
        // no internet: the shift saved on this device
        const copy = isNoAnswer(error) ? readCopy(shiftCopyName()) : undefined;
        if (copy === undefined)
          localErrorHandler({ namespace, error, stopLoading: "fetchCurrent" });
        yield put({ type: "setCurrent", payload: copy });
      } finally {
        yield put({ type: "stopLoading", loadingType: "fetchCurrent" });
      }
    },
    *addMovement({ payload }, { call, put }) {
      yield put({ type: "startLoading", loadingType: "addMovement" });
      try {
        yield call(shiftService.addMovement, payload.shiftId, payload.data);
        const response = yield call(shiftService.fetchCurrent);
        saveCopy(shiftCopyName(), response.data || null);
        yield put({ type: "setCurrent", payload: response.data });
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "addMovement" });
      } finally {
        yield put({ type: "stopLoading", loadingType: "addMovement" });
      }
    },
    *close({ payload }, { call, put }) {
      yield put({ type: "startLoading", loadingType: "close" });
      try {
        const response = yield call(
          shiftService.close,
          payload.shiftId,
          payload.data,
        );
        saveCopy(shiftCopyName(), response.data || null);
        yield put({ type: "setCurrent", payload: response.data });
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "close" });
      } finally {
        yield put({ type: "stopLoading", loadingType: "close" });
      }
    },
  },
  reducers: {
    setCurrent(state, { payload }) {
      return { ...state, current: payload };
    },
    startLoading(state, { loadingType }) {
      return { ...state, loading: { ...state.loading, [loadingType]: true } };
    },
    stopLoading(state, { loadingType }) {
      return { ...state, loading: { ...state.loading, [loadingType]: false } };
    },
  },
};
